import {
  createActionsHook,
  createHook,
  createStateHook,
  createStore,
} from "react-sweet-state";
import moment from "moment";
import { LoadStatus, Notification } from "types/types";
import {
  ImportedInvoice,
  ImportedInvoiceEntry,
  ImportedInvoiceEntryMetadataMap,
  ImportedInvoiceImportResult,
  ImportedInvoiceMetadataMap,
} from "types/importedInvoices";
import {
  applyImportedInvoices,
  deleteImportedInvoices,
  filterImportedInvoiceEntries,
  filterImportedInvoices,
  importImportedInvoices,
  loadImportedInvoices,
  pullImportedInvoices,
  rematchImportedInvoices,
  removeImportedInvoices,
  selectAllImportedInvoices,
  selectImportedInvoice,
  setStoreValue,
  setStoreValues,
  toggleImportedInvoiceSelection,
} from "./ImportedInvoicesStoreActions";
import { setNotificationMessage } from "./AppStoreActions";

// Master/detail page for the invoices imported from QTG: `importedInvoices` holds the
// invoices of the current query together with their entries (the query returns both).
// Several invoices can be ticked (metadata `selected`; re-match / delete act on all of
// them); `selectedImportedInvoiceId` is the invoice ticked last, whose entries
// (`importedInvoiceEntries`) fill the detail pane.
export type StoreState = {
  notifications: Notification[];
  importedInvoices: ImportedInvoice[];
  importedInvoiceEntries: ImportedInvoiceEntry[];
  importedInvoiceMetadataMap: ImportedInvoiceMetadataMap;
  importedInvoiceEntryMetadataMap: ImportedInvoiceEntryMetadataMap;
  selectedImportedInvoiceId: number | null;
  importedInvoicesSelectAll: boolean; // header checkbox state

  importedInvoicesLoadStatus: LoadStatus;
  importImportedInvoicesStatus: LoadStatus;
  pullImportedInvoicesStatus: LoadStatus;
  rematchImportedInvoicesStatus: LoadStatus;
  deleteImportedInvoiceStatus: LoadStatus;

  searchTerm: string; // master grid (invoices)
  entriesSearchTerm: string; // detail grid (entries of the selected invoice)
  fromDateFilter: Date;
  toDateFilter: Date;
  matchStateFilter: string; // "" = all

  importedInvoicesSort: any;
  importedInvoiceEntriesSort: any;

  importImportedInvoicesDialog: {
    isOpen: boolean;
  };
  lastImportResult: ImportedInvoiceImportResult | null;
  pullImportedInvoicesDialog: {
    isOpen: boolean;
  };
  lastPullResult: ImportedInvoiceImportResult | null;
  rematchImportedInvoicesDialog: {
    isOpen: boolean;
    importedInvoiceIds: number[] | null; // null = every invoice that is not fully matched
  };
  deleteImportedInvoiceDialog: {
    isOpen: boolean;
    importedInvoiceIds: number[];
  };
};

const initialState: StoreState = {
  notifications: [],
  importedInvoices: [],
  importedInvoiceEntries: [],
  importedInvoiceMetadataMap: {},
  importedInvoiceEntryMetadataMap: {},
  selectedImportedInvoiceId: null,
  importedInvoicesSelectAll: false,

  importedInvoicesLoadStatus: "none",
  importImportedInvoicesStatus: "none",
  pullImportedInvoicesStatus: "none",
  rematchImportedInvoicesStatus: "none",
  deleteImportedInvoiceStatus: "none",

  searchTerm: "",
  entriesSearchTerm: "",
  fromDateFilter: moment().subtract(3, "months").startOf("day").toDate(),
  toDateFilter: moment().toDate(),
  matchStateFilter: "",

  importedInvoicesSort: null,
  importedInvoiceEntriesSort: null,

  importImportedInvoicesDialog: {
    isOpen: false,
  },
  lastImportResult: null,
  pullImportedInvoicesDialog: {
    isOpen: false,
  },
  lastPullResult: null,
  rematchImportedInvoicesDialog: {
    isOpen: false,
    importedInvoiceIds: null,
  },
  deleteImportedInvoiceDialog: {
    isOpen: false,
    importedInvoiceIds: [],
  },
};

const actions = {
  setImportedInvoicesStoreValue: setStoreValue,
  setImportedInvoicesStoreValues: setStoreValues,
  loadImportedInvoices: loadImportedInvoices,
  selectImportedInvoice: selectImportedInvoice,
  toggleImportedInvoiceSelection: toggleImportedInvoiceSelection,
  selectAllImportedInvoices: selectAllImportedInvoices,
  filterImportedInvoices: filterImportedInvoices,
  filterImportedInvoiceEntries: filterImportedInvoiceEntries,
  importImportedInvoices: importImportedInvoices,
  pullImportedInvoices: pullImportedInvoices,
  rematchImportedInvoices: rematchImportedInvoices,
  deleteImportedInvoices: deleteImportedInvoices,
  applyImportedInvoices: applyImportedInvoices,
  removeImportedInvoices: removeImportedInvoices,
  setNotificationMessage: setNotificationMessage,
};

const ImportedInvoicesStore = createStore({
  initialState,
  actions,
  name: "ImportedInvoicesStore",
});

export const useImportedInvoicesStore = createHook(ImportedInvoicesStore);
export const useImportedInvoicesStoreState =
  createStateHook(ImportedInvoicesStore);
export const useImportedInvoicesStoreActions =
  createActionsHook(ImportedInvoicesStore);
